/******************************************************************************
 * MachineReferentialList: every active machine type or brand, with a rename.
 *
 * Duplicate groups only show rows the detector clustered together. A label that
 * is simply wrong — CASE I.H when the brand is CASE IH, and no twin to merge
 * into — never appears there. This list is that correction, on the same admin
 * tool. search-all already returns the enabled rows only.
 *****************************************************************************/

/* Imports */
import { useCallback, useEffect, useState } from "react";
import { t } from "i18next";
import toast from "react-hot-toast";
import { Button, Typography } from "@material-tailwind/react";
/***/

/* Components */
import Loading from "../../../../../../components/loading/Loading";
import MachineLabelEditor from "./MachineLabelEditor";
/***/

/* Services */
import { searchWithFilters } from "../../../../../../services/api/search";
import { showApiErrorToast } from "../../../../../../services/api/error";
import { EmptyMachineLabelError, machineReferentialResource, renameMachineReferential } from "./renameMachineReferential";
/***/

/**
* MachineReferentialList: editable list for one referential
* @param kind - types or brands
* @param reloadToken - bumped by the parent refresh button
*/
export default function MachineReferentialList({
  kind,
  reloadToken
}: {
  kind: EasyTech.MachineReferentialKind,
  reloadToken: number
}) {
  const [rows, setRows] = useState<{ id: string, label: string }[] | null>(null);
  const [query, setQuery] = useState("");
  const [loadError, setLoadError] = useState(false);

  const load = useCallback(() => {
    setRows(null);
    setLoadError(false);
    searchWithFilters<{ id: string, label: string }>(machineReferentialResource(kind), {
      queries: [],
      order: { by: "label", direction: "ASC" }
    }, true, true, true)
      .then((ret) => setRows(ret.data))
      .catch((err) => {
        console.error(err);
        showApiErrorToast(err, { fallbackMessage: t("admin:tools.machineDedup.errors.loadCatalog") });
        setLoadError(true);
      });
  }, [kind]);

  useEffect(() => {
    load();
  }, [load, reloadToken]);

  const needle = query.trim().toLowerCase();
  const visible = (rows || []).filter((row) => !needle || row.label.toLowerCase().includes(needle));

  /**
  * rename: persist a corrected label and reload the list
  */
  const rename = (id: string, label: string) => {
    return renameMachineReferential(kind, id, label).then(() => {
      toast.success(t("admin:tools.machineDedup.rename.success"));
      load();
    }).catch((err) => {
      if (err instanceof EmptyMachineLabelError) {
        toast.error(t("admin:tools.machineDedup.rename.empty"));
      } else {
        console.error(err);
        showApiErrorToast(err, { fallbackMessage: t("admin:tools.machineDedup.errors.rename") });
      }
      throw err;
    });
  };
  /***/

  if (loadError) {
    return (
      <div className="flex flex-col items-start gap-2" data-testid="catalog-error">
        <Typography variant="small" color="red">
          {t("admin:tools.machineDedup.errors.loadCatalog")}
        </Typography>
        <Button size="sm" variant="outlined" data-testid="catalog-retry" onClick={load}>
          {t("common:retry")}
        </Button>
      </div>
    );
  }

  if (!rows) return <Loading />;

  return (
    <div>
      <div className="flex items-center justify-between gap-4 mb-4">
        <input
          data-testid="catalog-search"
          value={query}
          placeholder={t("admin:tools.machineDedup.catalog.search")}
          aria-label={t("admin:tools.machineDedup.catalog.search")}
          onChange={(event) => setQuery(event.target.value)}
          className="border border-blue-gray-200 rounded px-3 py-2 text-sm w-full max-w-sm" />
        <Typography variant="small" data-testid="catalog-count">
          {t("admin:tools.machineDedup.catalog.count", { count: visible.length })}
        </Typography>
      </div>

      {visible.length === 0 ? (
        <Typography variant="small" data-testid="catalog-empty">
          {t("admin:tools.machineDedup.catalog.empty")}
        </Typography>
      ) : (
        <ul className="flex flex-col">
          {visible.map((row) => (
            <li key={row.id} data-testid={"ref-" + row.id}
              className="flex items-center justify-between gap-4 py-2 border-b border-blue-gray-50">
              <span>{row.label}</span>
              <MachineLabelEditor label={row.label} testId={row.id} onSave={(label) => rename(row.id, label)} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
/***/
